/**
 * V9 · Lucky Ducky Math Profiles Admin Tab (VIEW-ONLY)
 * Compliance:
 *   · No raw RTP slider anywhere.
 *   · No free-form % input.
 *   · Only APPROVED profiles are activatable (button gated on backend too).
 *   · Displays profile ID, version, validated RTP, sample size, volatility,
 *     configuration hash, approval status, active status.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ShieldCheck, PlayCircle, History, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  DRAFT:    'bg-gray-500/20 text-gray-300 border-gray-500/40',
  TESTED:   'bg-amber-500/20 text-amber-200 border-amber-500/40',
  APPROVED: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  ACTIVE:   'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
  RETIRED:  'bg-rose-500/20 text-rose-200 border-rose-500/40',
};

export const LuckyDuckyProfilesAdminTab = () => {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/admin/lucky-ducky/profiles`,   { withCredentials: true }),
        axios.get(`${API}/admin/lucky-ducky/audit-log?limit=20`, { withCredentials: true }),
      ]);
      setData(p.data); setAudit(a.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Lucky Ducky profiles');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async (profileId) => {
    if (!window.confirm(`Activate '${profileId}'? This affects FUTURE rounds only.`)) return;
    try {
      await axios.post(`${API}/admin/lucky-ducky/profiles/${profileId}/activate`, {}, { withCredentials: true });
      toast.success(`Activated ${profileId}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Activation failed'); }
  };

  const rollback = async () => {
    if (!window.confirm('Rollback to previously active profile?')) return;
    try {
      const r = await axios.post(`${API}/admin/lucky-ducky/rollback`, {}, { withCredentials: true });
      toast.success(`Rolled back to ${r.data.rolled_back_to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollback failed'); }
  };

  const profiles = data?.profiles || [];
  const activeId = data?.active_profile_id;

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5 space-y-4"
         data-testid="lucky-ducky-profiles-panel">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck size={20} className="text-emerald-300" />
        <h2 className="text-white text-lg font-black tracking-wide">Lucky Ducky · Math Profiles</h2>
        <div className="ml-auto flex gap-2">
          <button onClick={load} disabled={loading}
                  className="flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-xs text-white disabled:opacity-50"
                  data-testid="ld-profiles-refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={rollback}
                  className="flex items-center gap-1 rounded-full bg-rose-500/20 hover:bg-rose-500/30 px-3 py-1 text-xs text-rose-200 border border-rose-500/40"
                  data-testid="ld-profiles-rollback">
            <History size={12}/> Rollback
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3 text-xs text-white/80">
        <Lock size={14} className="text-emerald-300"/>
        <span>
          <strong className="text-emerald-300">Verified rules are locked</strong> — Cardinal 20 paylines, photographed paytable,
          5×3 layout, DOUBLE/WILD rules, BONUS scatter, 15 free plays & 3× feature multiplier are hard-coded in the engine and
          <strong> cannot </strong>be changed via a profile. Profiles ONLY carry ordered reel strips + per-reel symbol distribution.
          Only <strong className="text-emerald-300">APPROVED</strong> profiles can be activated; <strong>TESTED</strong>/<strong>DRAFT</strong> profiles cannot.
        </span>
      </div>

      {activeId && (
        <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-cyan-200">Currently Active Profile</div>
          <div className="text-cyan-100 text-base font-black" data-testid="ld-active-profile">{activeId}</div>
          <div className="text-[10px] text-cyan-200/80 mt-0.5 break-all">hash: {data?.active_hash}</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="ld-profiles-table">
          <thead>
            <tr className="text-white/50">
              <th className="text-left py-1">Profile</th>
              <th className="text-left py-1">Version</th>
              <th className="text-left py-1">Status</th>
              <th className="text-right py-1">Sim RTP</th>
              <th className="text-right py-1">Sample</th>
              <th className="text-right py-1">Volatility (stdev / p99 / p999)</th>
              <th className="text-left py-1">Config Hash</th>
              <th className="py-1"></th>
            </tr>
          </thead>
          <tbody>
            {profiles.length === 0 ? (
              <tr><td colSpan={8} className="text-white/40 italic py-4 text-center">No profiles yet.</td></tr>
            ) : profiles.map(p => {
              const isActive = p.profile_id === activeId;
              const sim = p.simulation || {};
              const vol = p.volatility || {};
              return (
                <tr key={p.profile_id} className={`border-t border-white/5 ${isActive ? 'bg-cyan-500/5' : ''}`}
                    data-testid={`ld-profile-row-${p.profile_id}`}>
                  <td className="py-1 text-white font-semibold">{p.profile_id}</td>
                  <td className="py-1 text-white/80">{p.profile_version}</td>
                  <td className="py-1">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[p.status] || ''}`}
                          data-testid={`ld-profile-status-${p.profile_id}`}>
                      {p.status}{isActive ? ' · ACTIVE' : ''}
                    </span>
                  </td>
                  <td className="py-1 text-right text-white/80">
                    {sim.simulated_total_rtp_pct != null ? `${sim.simulated_total_rtp_pct.toFixed(2)}%` : '—'}
                  </td>
                  <td className="py-1 text-right text-white/60">
                    {sim.sample_size ? sim.sample_size.toLocaleString() : '—'}
                  </td>
                  <td className="py-1 text-right text-white/60">
                    {vol.stdev_credits ?? '—'} / {vol.p99_win ?? '—'} / {vol.p999_win ?? '—'}
                  </td>
                  <td className="py-1 text-white/40 font-mono text-[10px] break-all max-w-[220px]">
                    {p.configuration_hash?.slice(0, 22)}…
                  </td>
                  <td className="py-1 text-right">
                    <button
                      onClick={() => activate(p.profile_id)}
                      disabled={p.status !== 'APPROVED' || isActive}
                      className="rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-30 disabled:cursor-not-allowed px-3 py-1 text-[11px] text-emerald-200 border border-emerald-500/40 inline-flex items-center gap-1"
                      title={p.status !== 'APPROVED' ? 'Only APPROVED profiles can be activated' : 'Activate'}
                      data-testid={`ld-activate-${p.profile_id}`}
                    >
                      <PlayCircle size={12}/> Activate
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="flex items-center gap-2 mb-2">
          <History size={16} className="text-amber-300"/>
          <h3 className="text-white text-sm font-black tracking-wide">Recent Activation / Rollback Audit</h3>
        </div>
        {(!audit?.entries || audit.entries.length === 0) ? (
          <div className="text-white/40 text-xs italic py-2">No audit entries yet.</div>
        ) : (
          <table className="w-full text-xs" data-testid="ld-profiles-audit">
            <thead>
              <tr className="text-white/50">
                <th className="text-left py-1">When</th>
                <th className="text-left py-1">Action</th>
                <th className="text-left py-1">Admin</th>
                <th className="text-left py-1">From → To</th>
                <th className="text-left py-1">Hash</th>
              </tr>
            </thead>
            <tbody>
              {audit.entries.map((e) => (
                <tr key={e._id} className="border-t border-white/5">
                  <td className="py-1 text-white/70">{new Date(e.at).toLocaleString()}</td>
                  <td className="py-1 text-white">{e.action}</td>
                  <td className="py-1 text-white/80">{e.admin_username || e.admin_user_id?.slice(0,8)}</td>
                  <td className="py-1 text-white/60">
                    {e.previous_profile || e.from_profile || '—'} → {e.new_profile || e.to_profile || '—'}
                  </td>
                  <td className="py-1 text-white/40 font-mono text-[10px] break-all max-w-[160px]">
                    {e.configuration_hash?.slice(0, 22)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LuckyDuckyProfilesAdminTab;
